export * from "fp-ts/lib/Record";
import * as R from "fp-ts/lib/Record";

import type * as O from "./Option";
import { type Ord, preserve } from "./Ord";

export type NonEmptyRecord<A, K extends keyof A = keyof A> =
  A extends Record<K, A[K]>
    ? K extends never
      ? never
      : A
    : never;

type NonNullableKeys<P> = Exclude<{
  [K in keyof P]: P[K] extends NonNullable<unknown>
    ? K
    : never;
}[keyof P], undefined>;

type NullableKeys<P> = Exclude<{
  [K in keyof P]: P[K] extends NonNullable<unknown>
    ? never
    : K
}[keyof P], undefined>;

export type NonNullableFields<P> = Pick<P, NonNullableKeys<P>> & Partial<Record<NullableKeys<P>, never>>;
export type NullableFields<P> = Pick<P, NullableKeys<P>>;

export type PartiallyPartialize<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type NullableToOptionalFields<P> = { [K in keyof P]-?: P[K] extends NonNullable<unknown> ? P[K] : O.Option<NonNullable<P[K]>>; };

const toEntriesWithOrd = (ord: Ord<string>): <K extends string, A>(r: Record<K, A>) => Array<[K, A]> => R.collect(ord)(
  (k, a) => [k, a]
);

export const toEntriesNoSort: <K extends string, A>(r: Record<K, A>) => Array<[K, A]> = toEntriesWithOrd(preserve());

type IndexOf<T, U> = T extends ReadonlyArray<unknown>
  ? never
  : T extends string
  ? string extends T
  ? never
  : _IndexOf<UnionToTuple<T>, U, 0>
  : never;

// Helper to convert union to tuple (preserves order)
type UnionToTuple<T> = UnionToIntersection<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends any ? (t: T) => T : never
// eslint-disable-next-line @typescript-eslint/no-explicit-any
> extends (t: infer U) => any
  ? readonly [...UnionToTuple<Exclude<T, U>>, U]
  : readonly [];

// Helper to convert union to intersection
type UnionToIntersection<U> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  U extends any ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

// Helper to find index in tuple
type _IndexOf<T extends ReadonlyArray<unknown>, U, I extends number = 0> =
  T extends readonly [infer H, ...infer Rest]
  ? H extends U
  ? I
  : _IndexOf<Rest, U, Increment<I>>
  : never;

// Helper to increment number
type Increment<N extends number> = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
][N];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type KeyToIndex<T extends Record<string, any>> = {
  [K in keyof T]: K extends keyof T
  ? IndexOf<keyof T, K>
  : never;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getKeyIndex = <T extends Record<string, any>>(
  key: keyof T
) => (
  obj: T
): KeyToIndex<T>[keyof T] => {
    const keys = Object.keys(obj);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return keys.indexOf(key as string) as KeyToIndex<T>[keyof T];
  };
